import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Modal, Nav, Spinner, Tab } from 'react-bootstrap';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas/loanApplication';
import { LoanDetails } from '../../../services/schemas/loanServiceSchemas/loanDetails';
import { downloadDocument } from '../../../services/documentService';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import DocumentValidityBadge from '../../common/DocumentValidityBadge';
import DocumentActionsMenu from '../../common/DocumentActionsMenu';
import VerificationSummary from '../../common/VerificationSummary';
import CreditAssessmentSummary from '../../common/CreditAssessmentSummary';
import CollateralSummary from '../../common/CollateralSummary';
import ApprovalConditionsCard from '../../common/ApprovalConditionsCard';
import LoanAgreementsCard from '../../common/LoanAgreementsCard';
import DisbursementsCard from '../../common/DisbursementsCard';
import { resolveForPreview, withExtension } from '../../../utils/documentPreview';
import {
  FUNDING_PRODUCT_COLUMN_SPANS,
  FundingFieldSpec,
  getFundingProduct,
  getFundingProductName
} from '../../../constants/fundingProducts';
import './ApplicationWorkspace.css';
import RetainedIndividualQuotation from '../../loan-application/steps/RetainedIndividualQuotation';
import { getDocumentUploadDate } from '../../../utils/documentDates';
import { LEGAL_ENTITY_TYPES } from '../../../utils/validation/legalEntityType';

/**
 * The application-level workspace: the application specific detail, its documents and the customer
 * behind it, each on its own tab so none of them crowds the others off the page.
 *
 * Everything shown here comes off the application record itself. Where a field was never captured
 * the row still renders, marked as not captured, so a reviewer picking the application up can see
 * what is outstanding rather than having to guess whether it was simply not displayed.
 */

interface ApplicationWorkspaceProps {
  application: LoanApplication;
}

/** Fields shown on the Loan request card, so the product card does not repeat them. */
const LOAN_REQUEST_FIELDS = [
  'loanAmount',
  'loanPurpose',
  'primaryRepaymentSource',
  'proposedRepaymentDate',
  'requiredFundingDate'
];

const NOT_CAPTURED = <span className="text-muted fst-italic">Not captured</span>;

const DetailField: React.FC<{ label: string; value?: React.ReactNode; span?: number; hint?: string }> = ({
  label,
  value,
  span = 6,
  hint
}) => (
  <div className={`col-md-${span} mb-3`}>
    <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>
      {label}
    </div>
    <div className="fw-medium text-break">
      {value === undefined || value === null || value === '' ? NOT_CAPTURED : value}
    </div>
    {hint && <small className="text-muted">{hint}</small>}
  </div>
);

const WorkspaceCard: React.FC<{
  title: string;
  icon: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, action, children }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
      <h6 className="mb-0 d-flex align-items-center">
        <span className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
          <i className={`bi ${icon} text-primary`}></i>
        </span>
        {title}
      </h6>
      {action}
    </Card.Header>
    <Card.Body className="p-4">{children}</Card.Body>
  </Card>
);

/** Renders a captured loan-details value the way the product design captured it. */
const formatFieldValue = (spec: FundingFieldSpec, loanDetails?: LoanDetails): string | undefined => {
  const raw = (loanDetails as any)?.[spec.name];
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (spec.kind === 'amount') return formatCurrency(Number(raw));
  if (spec.kind === 'date') return formatDate(String(raw));
  return String(raw);
};

/** Turns an API enum value such as CESSION_OF_PROCEEDS into readable text. */
const humanize = (value?: string): string => {
  if (!value) return '';
  return value.replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, letter => letter.toUpperCase());
};

const getDocumentIcon = (docType?: string) => {
  const type = docType?.split('/').pop()?.toLowerCase() || '';
  if (type.includes('pdf')) return 'bi-file-pdf';
  if (type.includes('doc') || type.includes('word')) return 'bi-file-word';
  if (type.includes('xls') || type.includes('excel')) return 'bi-file-excel';
  if (type.includes('jpg') || type.includes('jpeg') || type.includes('png') || type.includes('image')) return 'bi-file-image';
  if (docType?.toLowerCase().includes('id')) return 'bi-person-badge';
  if (docType?.toLowerCase().includes('financial')) return 'bi-file-earmark-spreadsheet';
  if (docType?.toLowerCase().includes('bank')) return 'bi-file-earmark-text';
  if (docType?.toLowerCase().includes('proof')) return 'bi-file-earmark-check';
  return 'bi-file-earmark';
};

const formatDocumentType = (docType?: string): string => {
  if (!docType) return 'Other';
  return docType.replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return 'Unknown';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

const getDocumentStatusBadge = (status?: string) => {
  const documentStatus = status || 'PENDING';
  switch (documentStatus) {
    case 'VERIFIED':
      return <Badge bg="success">Verified</Badge>;
    case 'PENDING':
      return <Badge bg="warning">Pending</Badge>;
    case 'REJECTED':
      return <Badge bg="danger">Rejected</Badge>;
    default:
      return <Badge bg="secondary">{documentStatus}</Badge>;
  }
};

const documentTableStyles: TableStyles = {
  table: {
    style: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
      borderRadius: '8px 8px 0 0',
    },
  },
  rows: {
    style: {
      minHeight: '60px',
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      borderBottomColor: '#e9ecef',
    },
    stripedStyle: {
      backgroundColor: '#fcfdff',
    },
  },
  cells: {
    style: {
      padding: '1rem',
    },
  },
};

// Business-facing wording for the stored codes, so a screen never shows SOLE_PROPRIETOR.
// One list of legal forms for the whole UI; see utils/validation/legalEntityType.
const ENTITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  LEGAL_ENTITY_TYPES.map(type => [type.code, type.label])
);

const REGISTRATION_NUMBER_TYPE_LABELS: Record<string, string> = {
  CIPC: 'CIPC',
  PRACTICE_NUMBER: 'Practice number',
  TRUST_NUMBER: 'Trust number',
  ID_NUMBER: 'ID number',
  OTHER: 'Other',
};

const REPRESENTATIVE_CAPACITY_LABELS: Record<string, string> = {
  DIRECTOR: 'Director',
  MEMBER: 'Member',
  TRUSTEE: 'Trustee',
  PARTNER: 'Partner',
  EMPLOYEE: 'Employee',
  ATTORNEY: 'Attorney',
  AGENT: 'Agent',
  OTHER: 'Other',
};

const ApplicationWorkspace: React.FC<ApplicationWorkspaceProps> = ({ application }) => {
  const [documentsVisible, setDocumentsVisible] = useState(true);
  const [downloadingDocumentKey, setDownloadingDocumentKey] = useState<string | null>(null);
  // Reviewers had to download every file before they could read it, which is what made verification
  // slow. The document is fetched once and shown in place; the object URL is released on close.
  const [preview, setPreview] = useState<{ name: string; kind: 'pdf' | 'image' | 'other'; url: string } | null>(null);
  const [previewingKey, setPreviewingKey] = useState<string | null>(null);

  const loanDetails = application.loanDetails;
  const company = application.company;
  const individual = application.individual;
  const documents = application.documents || [];
  const collaterals = application.collaterals || [];
  const bankAccount = application.bankAccount;
  const product = getFundingProduct(loanDetails?.fundingType);

  const handleDownloadDocument = async (documentKey: string, documentName?: string) => {
    try {
      setDownloadingDocumentKey(documentKey);
      const blob = await downloadDocument(documentKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      link.download = await withExtension(documentName || documentKey, blob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    } finally {
      setDownloadingDocumentKey(null);
    }
  };

  const handlePreviewDocument = async (documentKey: string, name?: string, contentType?: string) => {
    try {
      setPreviewingKey(documentKey);
      const blob = await downloadDocument(documentKey);
      const { kind, blob: typed } = await resolveForPreview(blob, name, contentType);

      setPreview({
        name: name || 'Document',
        kind,
        url: window.URL.createObjectURL(typed)
      });
    } catch (error) {
      console.error('Error previewing document:', error);
    } finally {
      setPreviewingKey(null);
    }
  };

  const closePreview = () => {
    if (preview) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const documentColumns = [
    {
      name: 'Document',
      selector: (row: any) => row.name || row.documentKey,
      sortable: true,
      grow: 2,
      cell: (row: any) => (
        <div className="d-flex align-items-center py-2">
          <i className={`bi ${getDocumentIcon(row.documentType)} me-3 text-primary fs-4`}></i>
          <div>
            <div className="fw-bold">{row.name || row.documentKey || 'N/A'}</div>
            <small className="text-muted">{formatDocumentType(row.documentType)}</small>
          </div>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: (row: any) => row.status || 'PENDING',
      sortable: true,
      cell: (row: any) => getDocumentStatusBadge(row.status),
      center: true,
      width: '120px',
    },
    {
      name: 'Uploaded',
      selector: (row: any) => row.createdDate || '',
      sortable: true,
      center: true,
      width: '170px',
      cell: (row: any) => (
        <div className="py-2 text-center">
          <div className="small">{getDocumentUploadDate(row) ? formatDate(getDocumentUploadDate(row)) : 'Unknown'}</div>
          <DocumentValidityBadge uploadDate={getDocumentUploadDate(row)} status={row.validityStatus} validUntil={row.validUntil} rule={row.validityRule} />
        </div>
      ),
    },
    {
      name: 'Size',
      selector: (row: any) => row.size || 0,
      sortable: true,
      cell: (row: any) => formatFileSize(row.size),
      center: true,
      width: '100px',
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <DocumentActionsMenu
          id={row.id || row.documentKey}
          actions={[
            {
              label: 'View',
              icon: 'fa-eye',
              busy: previewingKey === row.documentKey,
              disabled: !row.documentKey,
              onClick: () => handlePreviewDocument(row.documentKey, row.name, row.contentType)
            },
            {
              label: 'Download',
              icon: 'fa-download',
              busy: downloadingDocumentKey === row.documentKey,
              disabled: !row.documentKey,
              onClick: () => handleDownloadDocument(row.documentKey, row.name)
            }
          ]}
        />
      ),
      center: true,
      ignoreRowClick: true,
      width: '140px',
    },
  ];

  const proposedTerm = loanDetails
    ? (loanDetails.numberOfInstallments && loanDetails.numberOfInstallments > 1
        ? `${loanDetails.numberOfInstallments} months`
        : loanDetails.loanTerms
          ? `${loanDetails.loanTerms} days`
          : undefined)
    : undefined;

  const securityOffered = loanDetails?.securityTypesOffered?.length
    ? loanDetails.securityTypesOffered.map(type => humanize(type)).join(', ')
    : undefined;

  /** Product fields keep their design column span, minus anything the Loan request card shows. */
  const productFields = (product?.fields || [])
    .map((spec, index) => ({ spec, span: FUNDING_PRODUCT_COLUMN_SPANS[index] ?? 6 }))
    .filter(({ spec }) => !LOAN_REQUEST_FIELDS.includes(spec.name));

  /**
   * The funding request step captures a customer contribution for every product, but only some
   * products list it among their own fields (Asset Funding calls it a deposit). Fall back to the
   * Loan request card for the rest, so a figure that was captured is never silently hidden.
   */
  const productShowsContribution = (product?.fields || [])
    .some(spec => spec.name === 'customerContribution');

  return (
    <Tab.Container defaultActiveKey="customer">
      <RetainedIndividualQuotation application={application} />
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="pb-0 pt-3 px-3">
          <Nav variant="tabs" className="flex-wrap application-workspace-tabs">
            <Nav.Item>
              <Nav.Link eventKey="customer">
                <i className="bi bi-people me-2"></i>Customer &amp; accounts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="funding">
                <i className="bi bi-cash-coin me-2"></i>Funding details
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="documents">
                <i className="bi bi-folder me-2"></i>Documents
                {documents.length > 0 && (
                  <Badge bg="secondary" pill className="ms-2">{documents.length}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Body>
      </Card>

      <Tab.Content>
        {/* Funding details -------------------------------------------------------------- */}
        <Tab.Pane eventKey="funding">
          {/*
            Verification's conclusion belongs on the application itself, not only on the screen of
            whoever held the task. A rejected item recorded during verification is otherwise
            invisible to everyone downstream of it.
          */}
          <VerificationSummary applicationId={application.id} />
          <CollateralSummary applicationId={application.id} />
          {/* And credit's conclusion on top of it: the notes explain the terms the loan was given. */}
          <CreditAssessmentSummary applicationId={application.id} />
          {/* The committee's decision and the conditions still to be met, for anyone returning to it. */}
          <ApprovalConditionsCard applicationId={application.id} />
          {/* The pre-agreement and the client-signed agreement, with who recorded the signature and when. */}
          <LoanAgreementsCard applicationId={application.id} />
          {/* Each payout, who it went to, and its proof of payment (attachable once the bank sends it). */}
          <DisbursementsCard applicationId={application.id} />

          <div className="row g-4">
            <div className="col-xl-6">
              <WorkspaceCard title="Loan request" icon="bi-cash-coin">
                <div className="row">
                  <DetailField
                    label="Funding product"
                    value={loanDetails?.fundingType ? getFundingProductName(loanDetails.fundingType) : undefined}
                  />
                  <DetailField
                    label="Amount requested"
                    value={loanDetails?.loanAmount !== undefined ? (
                      <span className="fw-bold">{formatCurrency(loanDetails.loanAmount)}</span>
                    ) : undefined}
                  />
                  <DetailField label="Proposed term" value={proposedTerm} />
                  {!productShowsContribution && (
                    <DetailField
                      label="Customer contribution"
                      value={loanDetails?.customerContribution !== undefined
                        ? formatCurrency(loanDetails.customerContribution)
                        : undefined}
                    />
                  )}
                  <DetailField label="Required funding date" value={formatDate(loanDetails?.requiredFundingDate)} />
                  <DetailField label="Purpose" value={loanDetails?.loanPurpose} span={12} />
                  <DetailField label="Repayment source" value={loanDetails?.primaryRepaymentSource} span={12} />
                  <DetailField label="Proposed repayment date" value={formatDate(loanDetails?.proposedRepaymentDate)} />
                  <DetailField
                    label="Security offered"
                    value={securityOffered}
                    hint={loanDetails?.securityAvailability
                      ? `Customer indicated: ${humanize(loanDetails.securityAvailability)}`
                      : undefined}
                  />
                </div>
              </WorkspaceCard>
            </div>

            <div className="col-xl-6">
              {product ? (
                <WorkspaceCard
                  title={product.heading}
                  icon="bi-file-earmark-text"
                  action={<Badge bg="light" text="dark" className="border">{product.shortName}</Badge>}
                >
                  <p className="text-muted small">{product.blurb}</p>
                  <div className="row">
                    {productFields.map(({ spec, span }) => (
                      <DetailField
                        key={spec.name}
                        label={spec.label}
                        value={formatFieldValue(spec, loanDetails)}
                        span={span}
                      />
                    ))}
                  </div>
                </WorkspaceCard>
              ) : (
                <WorkspaceCard title="Product details" icon="bi-file-earmark-text">
                  <Alert variant="info" className="d-flex align-items-center mb-0">
                    <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                    <p className="mb-0">
                      {loanDetails?.fundingType
                        ? `No product-specific details are captured for ${getFundingProductName(loanDetails.fundingType)}.`
                        : 'No funding product was selected on this application, so there are no product-specific details to show.'}
                    </p>
                  </Alert>
                </WorkspaceCard>
              )}
            </div>

            <div className="col-12">
              <WorkspaceCard
                title="Collateral"
                icon="bi-shield-lock"
                action={collaterals.length > 0 ? (
                  <Badge bg="light" text="dark" className="border">
                    {collaterals.length} {collaterals.length === 1 ? 'item' : 'items'}
                  </Badge>
                ) : undefined}
              >
                {collaterals.length > 0 ? (
                  collaterals.map((collateral, index) => (
                    <div
                      key={collateral.registrationNumber || `${collateral.assetDescription}-${index}`}
                      className="border rounded p-3 mb-3 bg-light bg-opacity-50"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">
                          <i className="bi bi-shield text-primary me-2"></i>
                          {collateral.assetDescription || `Collateral #${index + 1}`}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          {collateral.verificationStatus && (
                            <Badge bg={collateral.verificationStatus === 'APPROVE' ? 'success' : 'danger'}>
                              {collateral.verificationStatus === 'APPROVE' ? 'Verified' : 'Rejected'}
                            </Badge>
                          )}
                          <Badge bg="light" text="dark" className="border">
                            {collateral.value !== undefined ? formatCurrency(collateral.value) : 'No value'}
                          </Badge>
                        </div>
                      </div>
                      <div className="row">
                        <DetailField label="Type" value={humanize(collateral.collateralType)} span={3} />
                        <DetailField label="Value" value={collateral.value !== undefined ? formatCurrency(collateral.value) : undefined} span={3} />
                        <DetailField label="Owner" value={collateral.ownerName} span={3} />
                        <DetailField label="Owner ID" value={collateral.ownerId} span={3} />
                        <DetailField label="Make" value={collateral.make} span={3} />
                        <DetailField label="Year model" value={collateral.yearModel} span={3} />
                        <DetailField label="Registration" value={collateral.registrationNumber} span={3} />
                        <DetailField label="Description" value={collateral.assetDescription} span={3} />
                        {collateral.otherDetails && (
                          <DetailField label="Other details" value={collateral.otherDetails} span={6} />
                        )}
                        {collateral.verificationNotes && (
                          <DetailField label="Verification notes" value={collateral.verificationNotes} span={6} />
                        )}
                      </div>

                      {/*
                        The report, checklist and photos are captured during collateral
                        verification and were only ever visible on that screen, so anyone opening
                        the application afterwards could not see the evidence behind the asset.
                      */}
                      {(() => {
                        const collateralDocuments = [
                          { key: collateral.collateralReportDocumentKey, label: 'Collateral report' },
                          { key: collateral.collateralChecklistDocumentKey, label: 'Verification checklist' },
                          { key: collateral.photoDocumentKey1, label: 'Photo 1' },
                          { key: collateral.photoDocumentKey2, label: 'Photo 2' },
                          { key: collateral.photoDocumentKey3, label: 'Photo 3' },
                          { key: collateral.photoDocumentKey4, label: 'Photo 4' },
                          { key: collateral.photoDocumentKey5, label: 'Photo 5' },
                          { key: collateral.photoDocumentKey6, label: 'Photo 6' }
                        ].filter(doc => !!doc.key);

                        if (collateralDocuments.length === 0) {
                          return (
                            <p className="text-muted small fst-italic mb-0 mt-2">
                              No report, checklist or photos captured for this asset.
                            </p>
                          );
                        }

                        return (
                          <div className="mt-3 pt-3 border-top">
                            <div className="text-muted small mb-2">Supporting evidence</div>
                            <div className="d-flex flex-wrap gap-2">
                              {collateralDocuments.map(doc => (
                                <div key={doc.key} className="btn-group btn-group-sm" role="group">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={previewingKey === doc.key}
                                    onClick={() => handlePreviewDocument(doc.key as string, doc.label)}
                                  >
                                    <i className="bi bi-eye me-1"></i>
                                    {doc.label}
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={downloadingDocumentKey === doc.key}
                                    onClick={() => handleDownloadDocument(doc.key as string, doc.label)}
                                    title={`Download ${doc.label}`}
                                  >
                                    <i className={`bi ${downloadingDocumentKey === doc.key ? 'bi-hourglass-split' : 'bi-download'}`}></i>
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))
                ) : (
                  <Alert variant="info" className="d-flex align-items-center mb-0">
                    <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                    <p className="mb-0">No collateral information provided for this application.</p>
                  </Alert>
                )}
              </WorkspaceCard>
            </div>
          </div>
        </Tab.Pane>

        {/* Documents -------------------------------------------------------------------- */}
        <Tab.Pane eventKey="documents">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 d-flex align-items-center">
                <i className="bi bi-folder text-primary me-2"></i>
                Documents
                {documents.length > 0 && (
                  <Badge bg="primary" pill className="ms-2 px-3">{documents.length} items</Badge>
                )}
              </h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setDocumentsVisible(visible => !visible)}
                aria-expanded={documentsVisible}
              >
                <i className={`bi ${documentsVisible ? 'bi-eye-slash' : 'bi-eye'} me-2`}></i>
                {documentsVisible ? 'Hide documents' : 'Show documents'}
              </Button>
            </Card.Header>
            {documentsVisible && (
              <Card.Body className="p-4">
                {documents.length > 0 ? (
                  <DataTable
                    columns={documentColumns}
                    data={documents}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                    responsive
                    striped
                    highlightOnHover
                    customStyles={documentTableStyles}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="bi bi-folder2-open text-muted" style={{ fontSize: '2.5rem' }}></i>
                        <p className="mt-2 mb-0 text-muted">No documents uploaded for this application.</p>
                      </div>
                    }
                  />
                ) : (
                  <Alert variant="info" className="d-flex align-items-center mb-0">
                    <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                    <p className="mb-0">No documents have been uploaded for this application.</p>
                  </Alert>
                )}
              </Card.Body>
            )}
          </Card>
        </Tab.Pane>

        {/* Customer & accounts ---------------------------------------------------------- */}
        <Tab.Pane eventKey="customer">
          <div className="row g-4">
            {company ? (
              <>
                <div className="col-xl-6">
                  <WorkspaceCard
                    title="Company information"
                    icon="bi-building"
                    action={application.customerNumber ? (
                      <Link
                        to={`/customers/${application.customerNumber}/accounts`}
                        className="text-decoration-none small fw-semibold"
                      >
                        Open customer accounts
                      </Link>
                    ) : undefined}
                  >
                    <div className="row">
                      <DetailField label="Company" value={company.companyName} />
                      <DetailField label="Customer number" value={application.customerNumber} />
                      <DetailField
                        label={`Registration${company.registrationNumberType ? ` (${REGISTRATION_NUMBER_TYPE_LABELS[company.registrationNumberType] ?? company.registrationNumberType})` : ''}`}
                        value={company.companyRegNumber}
                      />
                      <DetailField
                        label="Entity Type"
                        value={company.legalEntityType
                          ? (ENTITY_TYPE_LABELS[company.legalEntityType] ?? company.legalEntityType)
                          : undefined}
                      />
                      <DetailField
                        label="Customer type"
                        value={application.loanType ? humanize(application.loanType) : undefined}
                      />
                      <DetailField label="Tax number" value={company.taxNumber} />
                      <DetailField label="VAT number" value={company.vatNumber} />
                      <DetailField label="Industry sector" value={company.industrySector} />
                      <DetailField
                        label="Annual turnover"
                        value={company.turnover !== undefined ? formatCurrency(company.turnover) : undefined}
                      />
                      <DetailField label="Reason for funding" value={company.reasonForFunding} span={12} />
                      <DetailField label="KYC last updated" value={formatDate(company.kycLastUpdated)} />
                    </div>
                  </WorkspaceCard>
                </div>

                <div className="col-xl-6">
                  <WorkspaceCard
                    title="Directors & representative"
                    icon="bi-people"
                    action={company.directors?.length ? (
                      <Badge bg="light" text="dark" className="border">
                        {company.directors.length} {company.directors.length === 1 ? 'director' : 'directors'}
                      </Badge>
                    ) : undefined}
                  >
                    <div className="row">
                      <DetailField
                        label="Representative"
                        value={[company.contactPersonFirstName, company.contactPersonLastName].filter(Boolean).join(' ')}
                      />
                      <DetailField label="Role" value={company.representativeOccupation} />
                      <DetailField
                        label="Mobile"
                        value={company.contactTel ? (
                          <a href={`tel:${company.contactTel}`} className="text-decoration-none">{company.contactTel}</a>
                        ) : undefined}
                      />
                      <DetailField
                        label="Email"
                        value={company.contactEmail ? (
                          <a href={`mailto:${company.contactEmail}`} className="text-decoration-none">{company.contactEmail}</a>
                        ) : undefined}
                      />
                    </div>

                    {company.directors?.length ? (
                      <div className="mt-2">
                        {company.directors.map((director, index) => (
                          <div
                            key={director.idNumber || `${director.firstName}-${index}`}
                            className="border rounded p-3 mb-3 bg-light bg-opacity-50"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="fw-bold">
                                <i className="bi bi-person-badge text-primary me-2"></i>
                                {[director.firstName, director.lastName].filter(Boolean).join(' ')}
                              </span>
                              {director.isExecutive && <Badge bg="primary">Executive</Badge>}
                            </div>
                            <div className="row">
                              <DetailField label="ID number" value={director.idNumber} />
                              <DetailField label="ID type" value={director.idType ? humanize(director.idType) : undefined} />
                              <DetailField label="Designation" value={director.designation} />
                              <DetailField
                                label="Ownership"
                                value={director.ownershipPercentage !== undefined ? `${director.ownershipPercentage}%` : undefined}
                              />
                              <DetailField label="Nationality" value={director.nationality} />
                              <DetailField label="Appointed" value={formatDate(director.appointmentDate)} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Alert variant="info" className="d-flex align-items-center mb-0 mt-2">
                        <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                        <p className="mb-0">No directors captured for this customer.</p>
                      </Alert>
                    )}
                  </WorkspaceCard>
                </div>
              </>
            ) : individual ? (
              <>
                <div className="col-xl-6">
                  <WorkspaceCard
                    title="Personal information"
                    icon="bi-person-badge"
                    action={application.customerNumber ? (
                      <Link
                        to={`/customers/${application.customerNumber}/accounts`}
                        className="text-decoration-none small fw-semibold"
                      >
                        Open customer accounts
                      </Link>
                    ) : undefined}
                  >
                    <div className="row">
                      <DetailField label="Full names" value={individual.fullNames} />
                      <DetailField label="Customer number" value={application.customerNumber} />
                      <DetailField label="ID number" value={individual.idNumber} />
                      <DetailField
                        label="Customer type"
                        value={application.loanType ? humanize(application.loanType) : undefined}
                      />
                      <DetailField
                        label="Marital status"
                        value={individual.maritalStatus ? humanize(individual.maritalStatus) : undefined}
                      />
                      <DetailField label="Spouse" value={individual.spouseName} />
                      <DetailField
                        label="Mobile"
                        value={individual.cellNumber ? (
                          <a href={`tel:${individual.cellNumber}`} className="text-decoration-none">{individual.cellNumber}</a>
                        ) : undefined}
                      />
                      <DetailField
                        label="Email"
                        value={individual.email ? (
                          <a href={`mailto:${individual.email}`} className="text-decoration-none">{individual.email}</a>
                        ) : undefined}
                      />
                      <DetailField label="Residential address" value={individual.residentialAddress} span={12} />
                    </div>
                  </WorkspaceCard>
                </div>

                <div className="col-xl-6">
                  <WorkspaceCard title="Employment & next of kin" icon="bi-briefcase">
                    <div className="row">
                      <DetailField label="Employer" value={individual.employer} />
                      <DetailField label="Occupation" value={individual.occupation} />
                      <DetailField
                        label="Monthly salary"
                        value={individual.monthlySalary !== undefined ? formatCurrency(individual.monthlySalary) : undefined}
                      />
                      <DetailField label="Period employed" value={individual.periodEmployed} />
                      <DetailField label="Employer contact" value={individual.employerTel} />
                      <DetailField label="Employee number" value={individual.employeeNumber} />
                      <DetailField label="Employer address" value={individual.employerAddress} span={12} />
                      <DetailField label="Next of kin" value={individual.nextOfKin} />
                      <DetailField label="Next of kin contact" value={individual.nextOfKinTel} />
                    </div>
                  </WorkspaceCard>
                </div>
              </>
            ) : (
              <div className="col-12">
                <Alert variant="info" className="d-flex align-items-center mb-0">
                  <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                  <p className="mb-0">No customer information is linked to this application.</p>
                </Alert>
              </div>
            )}

            <div className="col-xl-6">
              <WorkspaceCard title="Bank account details" icon="bi-bank">
                {bankAccount ? (
                  <div className="row">
                    <DetailField label="Account holder" value={bankAccount.accountHolderName} />
                    <DetailField label="Bank" value={bankAccount.bankName} />
                    <DetailField
                      label="Account number"
                      value={bankAccount.accountNumber ? (
                        <span className="font-monospace">{bankAccount.accountNumber}</span>
                      ) : undefined}
                    />
                    <DetailField label="Account type" value={humanize(bankAccount.accountType)} />
                  </div>
                ) : (
                  <Alert variant="info" className="d-flex align-items-center mb-0">
                    <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                    <p className="mb-0">No bank account information has been provided for this application.</p>
                  </Alert>
                )}
              </WorkspaceCard>
            </div>
          </div>
        </Tab.Pane>
      </Tab.Content>

      <Modal show={!!preview} onHide={closePreview} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0 text-break">{preview?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '78vh' }}>
          {preview && (
            preview.kind === 'pdf' ? (
              <iframe title={preview.name} src={preview.url} style={{ width: '100%', height: '100%', border: 0 }} />
            ) : preview.kind === 'image' ? (
              <div className="d-flex justify-content-center align-items-start h-100 overflow-auto p-3">
                <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%' }} />
              </div>
            ) : (
              <Alert variant="info" className="m-4">
                <i className="bi bi-info-circle-fill me-2"></i>
                This file type cannot be shown here. Download it to open it.
              </Alert>
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          {preview && (
            <a className="btn btn-outline-primary" href={preview.url} download={preview.name}>
              <i className="bi bi-cloud-arrow-down me-2"></i>
              Download
            </a>
          )}
          <Button variant="secondary" onClick={closePreview}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Tab.Container>
  );
};

export default ApplicationWorkspace;
